import React, { useEffect, useState } from "react";
import {
  IconButton,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  Tooltip,
  Checkbox,
  TextField,
} from "@mui/material";
import FolderIcon from "@mui/icons-material/Folder";
import DataTable, { createTheme } from "react-data-table-component";

import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import CTLDialogContent from "application/components/styles/CTLDialogContent";
import CTLNotification from "application/components/CTLNotification";
import { useMaterialUIController } from "context";
import { application } from "globals/endpoints";
import colors from "assets/theme-dark/base/colors";

function LDapDirectory({ row, Update }) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const Extension = row?.original?.extension || "";

  const [openDialog, setOpenDialog] = useState(false);
  const [lDapData, setLDapData] = useState([]);
  const [clearSelectedRows, setClearSelectedRows] = useState(false);
  const [searchText, setSearchText] = useState("");

  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
    pagename: "",
    status: "",
  });

  const filteredData = lDapData.filter((item) =>
    item.contactNo?.toLowerCase().includes(searchText.toLowerCase())
  );
  useEffect(() => {
    if (!openDialog) {
      setNotify({
        isOpen: false,
        message: "",
        type: "",
        pagename: "",
        status: "",
      });
    }
  }, [openDialog]);

  const handleOpen = () => setOpenDialog(true);

  const handleCloseDialog = () => {
    const resetData = lDapData.map((item) => ({
      ...item,
      addToCall: false,
      addToSms: false,
    }));
    setLDapData(resetData);
    setOpenDialog(false);
    setSearchText("");
  };

  const GetLDapData = async () => {
    try {
      const response = await application.post(
        "extensionsSecondary/codeBlue/directoryList"
      );
      const rawData = response?.data?.data;

      const withFlags = Array.isArray(rawData)
        ? rawData.map((item) => ({
            ...item,
            addToCall: false,
            addToSms: false,
          }))
        : [];

      setLDapData(withFlags);
    } catch (error) {
      console.error("Error while fetching LDAP data:", error);
      setNotify({
        isOpen: true,
        message: "Failed to fetch LDAP data",
        type: "error",
        pagename: "LDapDirectory",
      });
    }
  };

  useEffect(() => {
    GetLDapData();
  }, []);

  const handleCheckboxChange = (index, key) => {
    if (!lDapData?.[index]) return;
    const updatedData = [...lDapData];
    updatedData[index][key] = !updatedData[index][key];
    setLDapData(updatedData);
  };

  const handleSubmit = () => {
    const smsNumber = lDapData
      .filter((item) => item?.addToCall && item?.contactNo)
      .map((item) => item.contactNo)
      .join(",");

    const smsText = lDapData
      .filter((item) => item?.addToSms && item?.contactNo)
      .map((item) => item.contactNo)
      .join(",");

    const data = {
      extension: Extension,
      smsNumber,
      smsText,
    };

    application
      .post("/extensionsSecondary/codeBlue/update", data)
      .then((response) => {
        setNotify({
          isOpen: true,
          status: `${response?.data?.message || "Success"} ${
            response?.data?.messageDetail || ""
          }`,
          type: "success",
        });

        const resetData = lDapData.map((item) => ({
          ...item,
          addToCall: false,
          addToSms: false,
        }));

        setTimeout(() => {
          setOpenDialog(false);
          setLDapData(resetData);
          setSearchText("");
          Update();
        }, 1000);
      })
      .catch((error) => {
        const message = error?.response?.data?.message || "Failed";
        const detail = error?.response?.data?.messageDetail || "";
        setNotify({
          isOpen: true,
          status: `${message} ${detail}`,
          type: "error",
          pagename: "Code Blue",
        });
      });
  };

  createTheme(
    "modifiedDarkTheme",
    {
      striped: { default: colors.background.default },
      selected: { default: colors.background.default },
      background: { default: colors.background.card },
    },
    "dark"
  );

  const AssociateColumns = [
    {
      name: "Contact No",
      selector: (row) => row?.contactNo || "-",
      sortable: true,
    },
    {
      name: "Add to Call",
      cell: (row, index) => (
        <Checkbox
          checked={!!row?.addToCall}
          onChange={() => handleCheckboxChange(index, "addToCall")}
        />
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
    {
      name: "Add to SMS",
      cell: (row, index) => (
        <Checkbox
          checked={!!row?.addToSms}
          onChange={() => handleCheckboxChange(index, "addToSms")}
        />
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  if (!row || !row.original) return null;

  return (
    <>
      <Tooltip title="LDAP-Directory">
        <IconButton color="success" onClick={handleOpen}>
          <FolderIcon />
        </IconButton>
      </Tooltip>

      <Dialog
        fullWidth
        maxWidth="lg"
        open={openDialog}
        onClose={handleCloseDialog}
        aria-labelledby="confirmation-dialog-title"
      >
        <DialogTitle sx={(theme) => CTLDialogContent(theme, { darkMode })}>
          Import LDAP Directory
        </DialogTitle>

        <DialogContent sx={(theme) => CTLDialogContent(theme, { darkMode })}>
          <MDBox
            sx={{
              mt: 3,
              width: "100%",
              minHeight: "300px",
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <TextField
              label="Search Contact"
              variant="outlined"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              fullWidth
              size="small"
            />
            {Array.isArray(filteredData) && filteredData.length > 0 ? (
              <DataTable
                clearSelectedRows={clearSelectedRows}
                columns={AssociateColumns}
                data={filteredData}
                responsive
                pagination
                striped
                paginationPerPage={10}
                paginationRowsPerPageOptions={[10, 25, 50]}
                theme={darkMode ? "modifiedDarkTheme" : "default"}
                fixedHeader
                persistTableHead
                fixedHeaderScrollHeight="66vh"
                selectableRowsHighlight
              />
            ) : (
              <MDTypography variant="body2">No data found.</MDTypography>
            )}
          </MDBox>
        </DialogContent>

        <DialogActions sx={(theme) => CTLDialogContent(theme, { darkMode })}>
          <MDButton onClick={handleCloseDialog} color="secondary">
            Cancel
          </MDButton>
          <MDButton onClick={handleSubmit} color="success" autoFocus>
            Submit
          </MDButton>
        </DialogActions>

        <CTLNotification notify={notify} setNotify={setNotify} />
      </Dialog>
    </>
  );
}

export default LDapDirectory;
